import { depth, type Entrypoint, type Level } from "../../client/entrypoint.ts";

export type NavKind = "zoom-in" | "zoom-out" | "sibling" | "jump";

export interface Match {
  readonly entry: Entrypoint;
  readonly params: Readonly<Record<string, string>>;
  readonly query: URLSearchParams;
  readonly url: string;
}

export interface Routes {
  match(url: string): Match | undefined;
  readonly entrypoints: readonly Entrypoint[];
}

const base = "http://nabla.local";

export function localUrl(href: string): string {
  const url = new URL(href, base);
  return `${url.pathname}${url.search}`;
}

export function compileRoutes(entrypoints: readonly Entrypoint[]): Routes {
  const seen = new Set<string>();
  for (const entry of entrypoints) {
    for (const name of [`id ${entry.id}`, `path ${entry.path}`]) {
      if (seen.has(name)) throw new Error(`two entrypoints register ${name}`);
      seen.add(name);
    }
  }
  const specificity = (path: string) => path.split("/").filter((part) => part !== "" && !part.startsWith(":")).length;
  const compiled = [...entrypoints].sort((a, b) => specificity(b.path) - specificity(a.path)).map((entry) => {
    const names: string[] = [];
    const source = entry.path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/:(\w+)/g, (_, name: string) => {
      names.push(name);
      return "([^/]+)";
    });
    return { entry, names, pattern: new RegExp(`^${source}/?$`) };
  });
  return {
    entrypoints,
    match(href) {
      const url = new URL(href, base);
      for (const { entry, names, pattern } of compiled) {
        const found = pattern.exec(url.pathname);
        if (!found) continue;
        const params = Object.fromEntries(names.map((name, index) => [name, decodeURIComponent(found[index + 1] ?? "")]));
        return { entry, params, query: url.searchParams, url: `${url.pathname}${url.search}` };
      }
      return undefined;
    },
  };
}

export function kindBetween(from: Level | undefined, to: Level | undefined, entered = false): NavKind {
  if (to === undefined) return "jump";
  const deeper = from === undefined ? 0 : depth[to] - depth[from];
  if (deeper === 0) return entered ? "zoom-in" : "jump";
  return deeper > 0 ? "zoom-in" : "zoom-out";
}

interface EntryState {
  readonly nabla: 1;
  readonly index: number;
  readonly prev?: string;
  readonly zoomed?: true;
  readonly scroll: number;
}

function isEntryState(value: unknown): value is EntryState {
  return typeof value === "object" && value !== null && "nabla" in value && value.nabla === 1 && "index" in value && typeof value.index === "number" && "scroll" in value && typeof value.scroll === "number";
}

export interface HistoryPort {
  url(): string;
  state(): unknown;
  push(url: string, state: EntryState): void;
  replace(url: string, state: EntryState): void;
  back(): void;
}

export interface Visit {
  readonly url: string;
  readonly kind: NavKind;
  readonly scroll: number | undefined;
  readonly back: boolean;
}

export interface Navigator {
  start(): Visit;
  go(href: string, scroll: number, kind?: NavKind): Visit | undefined;
  back(scroll: number): boolean;
  zoomOut(parent: string, scroll: number): Visit | undefined;
  redirect(href: string): Visit | undefined;
  popped(scroll: number): Visit;
  scrollOf(href: string): number | undefined;
}

function onlySorts(routes: Routes, shown: string, url: string): boolean {
  const [from, to] = [new URL(shown, base), new URL(url, base)];
  if (from.pathname !== to.pathname) return false;
  const replaceOn = routes.match(url)?.entry.replaceOn ?? [];
  const names = new Set([...from.searchParams.keys(), ...to.searchParams.keys()]);
  return [...names].every((name) => replaceOn.includes(name) || from.searchParams.get(name) === to.searchParams.get(name));
}

export function createNavigator(history: HistoryPort, routes: Routes): Navigator {
  const scrolls = new Map<string, number>();
  const current = (): EntryState => {
    const state = history.state();
    return isEntryState(state) ? state : { nabla: 1, index: 0, scroll: 0 };
  };
  let shown = { url: localUrl(history.url()), state: current() };
  const see = (url: string) => void (shown = { url, state: current() });
  const remember = (scroll: number) => {
    const state = current();
    scrolls.set(shown.url, scroll);
    history.replace(shown.url, { ...state, scroll });
  };
  const levelOf = (url: string) => routes.match(url)?.entry.level;

  function push(url: string, kind: NavKind, scroll: number): Visit {
    remember(scroll);
    const index = current().index + 1;
    history.push(url, { nabla: 1, index, prev: shown.url, scroll: 0, ...(kind === "zoom-in" ? { zoomed: true } : {}) });
    see(url);
    return { url, kind, scroll: kind === "zoom-out" ? scrolls.get(url) : undefined, back: false };
  }

  function replace(url: string, kind: NavKind): Visit {
    const state = current();
    history.replace(url, { ...state, scroll: 0 });
    see(url);
    return { url, kind, scroll: kind === "jump" ? undefined : 0, back: false };
  }

  return {
    start() {
      const state = history.state();
      if (!isEntryState(state)) history.replace(shown.url, { nabla: 1, index: 0, scroll: 0 });
      see(shown.url);
      return { url: shown.url, kind: "jump", scroll: isEntryState(state) ? state.scroll : undefined, back: false };
    },
    go(href, scroll, kind) {
      const url = localUrl(href);
      if (url === shown.url) return undefined;
      if (kind === "sibling") return replace(url, "sibling");
      if (onlySorts(routes, shown.url, url)) return replace(url, "jump");
      const how = kind ?? kindBetween(levelOf(shown.url), levelOf(url));
      if (how === "zoom-out" && current().prev === url && current().index > 0) {
        remember(scroll);
        history.back();
        return undefined;
      }
      return push(url, how, scroll);
    },
    back(scroll) {
      if (current().index === 0) return false;
      remember(scroll);
      history.back();
      return true;
    },
    zoomOut(parent, scroll) {
      return this.go(parent, scroll, "zoom-out");
    },
    redirect(href) {
      const url = localUrl(href);
      return url === shown.url ? undefined : replace(url, "jump");
    },
    popped(scroll) {
      scrolls.set(shown.url, scroll);
      const url = localUrl(history.url());
      const state = current();
      const back = state.index < shown.state.index;
      const left = back && shown.state.zoomed === true && shown.state.prev === url;
      const entered = !back && state.zoomed === true && state.prev === shown.url;
      const kind = left ? "zoom-out" : kindBetween(levelOf(shown.url), levelOf(url), entered);
      see(url);
      return { url, kind, scroll: scrolls.get(url) ?? state.scroll, back };
    },
    scrollOf: (href) => scrolls.get(localUrl(href)),
  };
}
